/**
 * Generic inline field-error plumbing for tanstack-form bound fields.
 * The form owner computes errors (e.g. from a Zod safeParse) and provides a
 * lookup; bound field components read their own error by field name.
 * Aliases: field errors context, inline form errors, useFieldError.
 */
import { createContext, useContext } from "react";

type ErrorLookup = (name: string) => string | undefined;

const FieldErrorsContext = createContext<ErrorLookup>(() => undefined);

export function FieldErrorsProvider({
  value,
  children,
}: {
  value: ErrorLookup;
  children: React.ReactNode;
}) {
  return (
    <FieldErrorsContext.Provider value={value}>
      {children}
    </FieldErrorsContext.Provider>
  );
}

/** Convert a tanstack bracketed field name (`tags[0].title`) to a Zod dot path (`tags.0.title`). */
export function toDotPath(name: string): string {
  return name.replace(/\[(\d+)\]/g, ".$1");
}

/** Returns the current error string for a bound field, or undefined. */
export function useFieldError(name: string): string | undefined {
  const lookup = useContext(FieldErrorsContext);
  return lookup(toDotPath(name));
}

/**
 * The lookup a form hands `FieldErrorsProvider`: the first Zod issue at each
 * field's path, once `show` is true (usually after a save was tried).
 */
export function zodErrorLookup(
  issues: readonly { path: readonly PropertyKey[]; message: string }[],
  show: boolean,
): ErrorLookup {
  return (name) =>
    show
      ? issues.find((issue) => issue.path.join(".") === toDotPath(name))
          ?.message
      : undefined;
}
